import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Dashboard.module.css';
import Layout from '../../Layout/Layout';
import { URL as BASEURL } from '../../AxiosUrlGlobal';

/**
 * Dashboard - Main dashboard for Super Admin
 * Shows platform-wide statistics and quick actions
 */
function Dashboard() {
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalStores: 0,
        activeSubscriptions: 0,
        trialUsers: 0,
        totalRevenue: 0,
        newSignupsToday: 0,
        totalOrders: 0,
        totalProducts: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [healthStatus, setHealthStatus] = useState({
        backend: { status: 'checking', lastCheck: null },
        dns: { status: 'checking', lastCheck: null },
        ssl: { status: 'checking', lastCheck: null },
        cdn: { status: 'checking', lastCheck: null },
        nginxManager: { status: 'checking', lastCheck: null },
        ispManager: { status: 'checking', lastCheck: null },
        order: { status: 'checking', lastCheck: null },
        frontend: { status: 'checking', lastCheck: null },
        postfix: { status: 'checking', lastCheck: null },
        dovecot: { status: 'checking', lastCheck: null },
        postfixSync: { status: 'checking', lastCheck: null },
        dovecotAuth: { status: 'checking', lastCheck: null }
    });

    useEffect(() => {
        fetchDashboardStats();
        fetchHealthStatus();

        const healthInterval = setInterval(() => {
            fetchHealthStatus();
        }, 5000);

        return () => clearInterval(healthInterval);
    }, []);

    const fetchDashboardStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('id');

            if (!userId) {
                setError('User ID not found. Please login again.');
                setLoading(false);
                return;
            }

            const response = await axios.get(`${BASEURL}superadmin/dashboard-stats/${userId}`, {
                headers: { token }
            });

            if (response.data.success) {
                setStats(response.data.data);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching dashboard stats:', err);
            setError('Failed to load dashboard statistics');
            setLoading(false);
        }
    };

    const fetchHealthStatus = async () => {
        const services = [
            { name: 'backend', url: `${BASEURL}health`, label: 'Backend API' },
            { name: 'dns', url: `${BASEURL}dns-health`, label: 'DNS Server' },
            { name: 'ssl', url: `${BASEURL}ssl-health`, label: 'SSL/Nginx Server' },
            { name: 'cdn', url: `${BASEURL}cdn-health`, label: 'CDN Server' },
            { name: 'nginxManager', url: `${BASEURL}nginx-manager-health`, label: 'Nginx Manager' },
            { name: 'ispManager', url: `${BASEURL}isp-manager-health`, label: 'ISP Manager' },
            { name: 'order', url: `${BASEURL}order-health`, label: 'Order Service' },
            { name: 'frontend', url: `${BASEURL}superadmin-health`, label: 'Root Website' },
            { name: 'postfix', url: `${BASEURL}postfix-health`, label: 'Postfix (SMTP)' },
            { name: 'dovecot', url: `${BASEURL}dovecot-health`, label: 'Dovecot (IMAP)' },
            { name: 'postfixSync', url: `${BASEURL}postfix-sync-health`, label: 'Postfix Sync' },
            { name: 'dovecotAuth', url: `${BASEURL}dovecot-auth-health`, label: 'Dovecot Auth' }
        ];

        const newHealthStatus = { ...healthStatus };

        for (const service of services) {
            try {
                const response = await axios.get(service.url, { timeout: 3000 });
                newHealthStatus[service.name] = {
                    status: 'healthy',
                    lastCheck: new Date().toISOString(),
                    data: response.data
                };
            } catch (error) {
                newHealthStatus[service.name] = {
                    status: 'error',
                    lastCheck: new Date().toISOString(),
                    error: error.message
                };
            }
        }

        setHealthStatus(newHealthStatus);
    };

    if (loading) {
        return (
            <Layout>
                <div className={styles.loading}>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Loading dashboard...</span>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className={styles.container}>
                    <div className={styles.error}>
                        <i className="fas fa-exclamation-circle"></i>
                        {error}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1>Platform Overview</h1>
                    <p>Monitor your platform's performance and key metrics</p>
                </div>

                {/* System Health Status */}
                <div className={styles.healthSection}>
                    <div className={styles.healthHeader}>
                        <h2>
                            <i className="fas fa-heartbeat" style={{ color: '#008060' }}></i>
                            System Health
                        </h2>
                        <span className={styles.autoRefresh}>Auto-refresh: 5s</span>
                    </div>
                    <div className={styles.healthGrid}>
                        {/* Backend Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-server"></i>
                                    Backend API
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.backend.status]}`}>
                                    {healthStatus.backend.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.backend.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.backend.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.backend.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.backend.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Database:</span>
                                        <span className={styles.detailValue}>
                                            {healthStatus.backend.data.database?.isConnected ? 'Connected' : 'Disconnected'}
                                        </span>
                                    </div>
                                    {healthStatus.backend.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>
                                                {Math.floor(healthStatus.backend.data.uptime / 60)}m
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.backend.error && (
                                <div className={styles.healthError}>{healthStatus.backend.error}</div>
                            )}
                            {healthStatus.backend.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.backend.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* DNS Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-globe"></i>
                                    DNS Server
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.dns.status]}`}>
                                    {healthStatus.dns.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.dns.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.dns.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.dns.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.dns.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Workers:</span>
                                        <span className={styles.detailValue}>{healthStatus.dns.data.workers}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Cached Records:</span>
                                        <span className={styles.detailValue}>{healthStatus.dns.data.cachedRecords}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Database:</span>
                                        <span className={styles.detailValue}>
                                            {healthStatus.dns.data.database?.isConnected ? 'Connected' : 'Disconnected'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {healthStatus.dns.error && (
                                <div className={styles.healthError}>{healthStatus.dns.error}</div>
                            )}
                            {healthStatus.dns.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.dns.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* SSL Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-lock"></i>
                                    SSL/Nginx Server
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.ssl.status]}`}>
                                    {healthStatus.ssl.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.ssl.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.ssl.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.ssl.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.ssl.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Database:</span>
                                        <span className={styles.detailValue}>
                                            {healthStatus.ssl.data.database?.isConnected ? 'Connected' : 'Disconnected'}
                                        </span>
                                    </div>
                                    {healthStatus.ssl.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>
                                                {Math.floor(healthStatus.ssl.data.uptime / 60)}m
                                            </span>
                                        </div>
                                    )}
                                    {healthStatus.ssl.data.pid && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>PID:</span>
                                            <span className={styles.detailValue}>{healthStatus.ssl.data.pid}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.ssl.error && (
                                <div className={styles.healthError}>{healthStatus.ssl.error}</div>
                            )}
                            {healthStatus.ssl.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.ssl.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* CDN Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-photo-video"></i>
                                    CDN Server
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.cdn.status]}`}>
                                    {healthStatus.cdn.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.cdn.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.cdn.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.cdn.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.cdn.data.status}</span>
                                    </div>
                                    {healthStatus.cdn.data.cache && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Cached Items:</span>
                                            <span className={styles.detailValue}>{healthStatus.cdn.data.cache.keys}</span>
                                        </div>
                                    )}
                                    {healthStatus.cdn.data.memory && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Memory (Heap):</span>
                                            <span className={styles.detailValue}>{healthStatus.cdn.data.memory.heapUsed}</span>
                                        </div>
                                    )}
                                    {healthStatus.cdn.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>
                                                {Math.floor(healthStatus.cdn.data.uptime / 60)}m
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.cdn.error && (
                                <div className={styles.healthError}>{healthStatus.cdn.error}</div>
                            )}
                            {healthStatus.cdn.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.cdn.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Nginx Manager Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-cogs"></i>
                                    Nginx Manager
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.nginxManager.status]}`}>
                                    {healthStatus.nginxManager.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.nginxManager.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.nginxManager.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.nginxManager.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.nginxManager.data.status}</span>
                                    </div>
                                    {healthStatus.nginxManager.data.nginx && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Nginx:</span>
                                            <span className={styles.detailValue}>{healthStatus.nginxManager.data.nginx.status}</span>
                                        </div>
                                    )}
                                    {healthStatus.nginxManager.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>{healthStatus.nginxManager.data.uptime.formatted}</span>
                                        </div>
                                    )}
                                    {healthStatus.nginxManager.data.memory && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Memory (Heap):</span>
                                            <span className={styles.detailValue}>{healthStatus.nginxManager.data.memory.heapUsed}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.nginxManager.error && (
                                <div className={styles.healthError}>{healthStatus.nginxManager.error}</div>
                            )}
                            {healthStatus.nginxManager.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.nginxManager.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* ISP Manager Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-network-wired"></i>
                                    ISP Manager
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.ispManager.status]}`}>
                                    {healthStatus.ispManager.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.ispManager.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.ispManager.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.ispManager.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.ispManager.data.status}</span>
                                    </div>
                                    {healthStatus.ispManager.data.service && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Service:</span>
                                            <span className={styles.detailValue}>{healthStatus.ispManager.data.service}</span>
                                        </div>
                                    )}
                                    {healthStatus.ispManager.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>{healthStatus.ispManager.data.uptime.formatted}</span>
                                        </div>
                                    )}
                                    {healthStatus.ispManager.data.networkInterfaces && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Interfaces:</span>
                                            <span className={styles.detailValue}>{healthStatus.ispManager.data.networkInterfaces}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.ispManager.error && (
                                <div className={styles.healthError}>{healthStatus.ispManager.error}</div>
                            )}
                            {healthStatus.ispManager.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.ispManager.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Order Service Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-shopping-cart"></i>
                                    Order Service
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.order.status]}`}>
                                    {healthStatus.order.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.order.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.order.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.order.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.order.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Mode:</span>
                                        <span className={styles.detailValue}>{healthStatus.order.data.mode}</span>
                                    </div>
                                    {healthStatus.order.data.workerId && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Worker ID:</span>
                                            <span className={styles.detailValue}>{healthStatus.order.data.workerId}</span>
                                        </div>
                                    )}
                                    {healthStatus.order.data.memory && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Memory (Heap):</span>
                                            <span className={styles.detailValue}>{healthStatus.order.data.memory.heapUsed}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.order.error && (
                                <div className={styles.healthError}>{healthStatus.order.error}</div>
                            )}
                            {healthStatus.order.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.order.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Root Website Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-globe"></i>
                                    Root Website
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.frontend.status]}`}>
                                    {healthStatus.frontend.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.frontend.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.frontend.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.frontend.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.frontend.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Service:</span>
                                        <span className={styles.detailValue}>{healthStatus.frontend.data.service}</span>
                                    </div>
                                    {healthStatus.frontend.data.uptime && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Uptime:</span>
                                            <span className={styles.detailValue}>{healthStatus.frontend.data.uptime}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.frontend.error && (
                                <div className={styles.healthError}>{healthStatus.frontend.error}</div>
                            )}
                            {healthStatus.frontend.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.frontend.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Postfix (SMTP) Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-paper-plane"></i>
                                    Postfix (SMTP)
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.postfix.status]}`}>
                                    {healthStatus.postfix.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.postfix.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.postfix.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.postfix.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.postfix.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Port:</span>
                                        <span className={styles.detailValue}>{healthStatus.postfix.data.port} ({healthStatus.postfix.data.protocol})</span>
                                    </div>
                                    {healthStatus.postfix.data.queueCount !== undefined && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Queue:</span>
                                            <span className={styles.detailValue}>{healthStatus.postfix.data.queueCount} emails</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.postfix.error && (
                                <div className={styles.healthError}>{healthStatus.postfix.error}</div>
                            )}
                            {healthStatus.postfix.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.postfix.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Dovecot (IMAP) Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-inbox"></i>
                                    Dovecot (IMAP)
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.dovecot.status]}`}>
                                    {healthStatus.dovecot.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.dovecot.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.dovecot.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.dovecot.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.dovecot.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Port:</span>
                                        <span className={styles.detailValue}>{healthStatus.dovecot.data.port} ({healthStatus.dovecot.data.protocol})</span>
                                    </div>
                                </div>
                            )}
                            {healthStatus.dovecot.error && (
                                <div className={styles.healthError}>{healthStatus.dovecot.error}</div>
                            )}
                            {healthStatus.dovecot.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.dovecot.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Postfix Sync Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-sync"></i>
                                    Postfix Sync
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.postfixSync.status]}`}>
                                    {healthStatus.postfixSync.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.postfixSync.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.postfixSync.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.postfixSync.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.postfixSync.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Service:</span>
                                        <span className={styles.detailValue}>{healthStatus.postfixSync.data.service}</span>
                                    </div>
                                    {healthStatus.postfixSync.data.accounts !== undefined && (
                                        <div className={styles.healthDetail}>
                                            <span className={styles.detailLabel}>Accounts:</span>
                                            <span className={styles.detailValue}>{healthStatus.postfixSync.data.accounts}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {healthStatus.postfixSync.error && (
                                <div className={styles.healthError}>{healthStatus.postfixSync.error}</div>
                            )}
                            {healthStatus.postfixSync.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.postfixSync.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>

                        {/* Dovecot Auth Health */}
                        <div className={styles.healthCard}>
                            <div className={styles.healthCardHeader}>
                                <div className={styles.healthServiceName}>
                                    <i className="fas fa-key"></i>
                                    Dovecot Auth
                                </div>
                                <div className={`${styles.healthStatus} ${styles[healthStatus.dovecotAuth.status]}`}>
                                    {healthStatus.dovecotAuth.status === 'checking' && (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Checking...
                                        </>
                                    )}
                                    {healthStatus.dovecotAuth.status === 'healthy' && (
                                        <>
                                            <i className="fas fa-check-circle"></i>
                                            Healthy
                                        </>
                                    )}
                                    {healthStatus.dovecotAuth.status === 'error' && (
                                        <>
                                            <i className="fas fa-exclamation-circle"></i>
                                            Error
                                        </>
                                    )}
                                </div>
                            </div>
                            {healthStatus.dovecotAuth.data && (
                                <div className={styles.healthDetails}>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Status:</span>
                                        <span className={styles.detailValue}>{healthStatus.dovecotAuth.data.status}</span>
                                    </div>
                                    <div className={styles.healthDetail}>
                                        <span className={styles.detailLabel}>Service:</span>
                                        <span className={styles.detailValue}>{healthStatus.dovecotAuth.data.service}</span>
                                    </div>
                                </div>
                            )}
                            {healthStatus.dovecotAuth.error && (
                                <div className={styles.healthError}>{healthStatus.dovecotAuth.error}</div>
                            )}
                            {healthStatus.dovecotAuth.lastCheck && (
                                <div className={styles.lastCheck}>
                                    Last check: {new Date(healthStatus.dovecotAuth.lastCheck).toLocaleTimeString()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className={styles.statsSection}>
                    <div className={styles.statsGrid}>
                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.info}`}>
                                <i className="fas fa-users"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>{stats.totalUsers.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Total Users</div>
                                </div>
                                {stats.newSignupsToday > 0 && (
                                    <div className={styles.statChange}>
                                        <i className="fas fa-arrow-up" style={{ color: '#008060' }}></i>
                                        +{stats.newSignupsToday} today
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.success}`}>
                                <i className="fas fa-store"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>{stats.totalStores.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Active Stores</div>
                                </div>
                                <div className={styles.statChange}>Multi-tenant platform</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.info}`}>
                                <i className="fas fa-credit-card"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>{stats.activeSubscriptions.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Active Subscriptions</div>
                                </div>
                                <div className={styles.statChange}>{stats.trialUsers} on trial</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.success}`}>
                                <i className="fas fa-dollar-sign"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>৳{stats.totalRevenue.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Total Revenue</div>
                                </div>
                                <div className={styles.statChange}>From subscriptions</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.warning}`}>
                                <i className="fas fa-shopping-cart"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>{stats.totalOrders.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Total Orders</div>
                                </div>
                                <div className={styles.statChange}>Across all stores</div>
                            </div>
                        </div>

                        <div className={styles.statCard}>
                            <div className={`${styles.statIcon} ${styles.info}`}>
                                <i className="fas fa-box"></i>
                            </div>
                            <div>
                                <div className={styles.statContent}>
                                    <div className={styles.statValue}>{stats.totalProducts.toLocaleString()}</div>
                                    <div className={styles.statLabel}>Total Products</div>
                                </div>
                                <div className={styles.statChange}>Listed on platform</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className={styles.actionsSection}>
                    <div className={styles.actionsHeader}>
                        <h2>
                            <i className="fas fa-bolt" style={{ color: '#f59e0b' }}></i>
                            Quick Actions
                        </h2>
                    </div>
                    <div className={styles.actionsBody}>
                        <div className={styles.actionsGrid}>
                            <a href="/superadmin/users" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.info}`}>
                                    <i className="fas fa-users-cog"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>Manage Users</div>
                                    <div className={styles.actionDescription}>View and manage all users</div>
                                </div>
                            </a>

                            <a href="/superadmin/stores" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.success}`}>
                                    <i className="fas fa-store-alt"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>Manage Stores</div>
                                    <div className={styles.actionDescription}>View all stores</div>
                                </div>
                            </a>

                            <a href="/superadmin/packages" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.warning}`}>
                                    <i className="fas fa-box-open"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>Manage Packages</div>
                                    <div className={styles.actionDescription}>Create and edit packages</div>
                                </div>
                            </a>

                            <a href="/superadmin/billing" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.success}`}>
                                    <i className="fas fa-file-invoice-dollar"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>Billing Overview</div>
                                    <div className={styles.actionDescription}>View billing records</div>
                                </div>
                            </a>

                            <a href="/superadmin/analytics" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.info}`}>
                                    <i className="fas fa-chart-line"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>System Analytics</div>
                                    <div className={styles.actionDescription}>Platform analytics</div>
                                </div>
                            </a>

                            <a href="/superadmin/settings" className={styles.actionCard}>
                                <div className={`${styles.actionIcon} ${styles.error}`}>
                                    <i className="fas fa-cog"></i>
                                </div>
                                <div className={styles.actionContent}>
                                    <div className={styles.actionTitle}>System Settings</div>
                                    <div className={styles.actionDescription}>Configure platform</div>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Recent Activity */}
                <div className={styles.activitySection}>
                    <div className={styles.activityHeader}>
                        <h2>
                            <i className="fas fa-clock" style={{ color: '#2c6ecb' }}></i>
                            Recent Activity
                        </h2>
                    </div>
                    <div className={styles.activityBody}>
                        <div className={styles.activityList}>
                            <div className={styles.activityItem}>
                                <i className="fas fa-user-plus success"></i>
                                <span className={styles.activityText}>
                                    <strong>{stats.newSignupsToday}</strong> new users signed up today
                                </span>
                            </div>
                            <div className={styles.activityItem}>
                                <i className="fas fa-shopping-bag info"></i>
                                <span className={styles.activityText}>
                                    Platform processing orders across <strong>{stats.totalStores}</strong> stores
                                </span>
                            </div>
                            <div className={styles.activityItem}>
                                <i className="fas fa-dollar-sign success"></i>
                                <span className={styles.activityText}>
                                    <strong>{stats.activeSubscriptions}</strong> active paying subscriptions
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Dashboard;
